import { useCallback, useEffect, useState } from 'react';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { PrescricaoDto, PrescricaoFilters } from 'domains/prescricao';
import { PrescricaoService } from 'services/PrescricaoService/PrescricaoService';

import styles from './PrescricaoModal.module.css';

interface PrescricaoModalProps {
  open: boolean;
  onClose: () => void;
  crm: string;
}

const formatPeriod = (period: string) => {
  const match = period.match(/^(\d{4})-(0[1-9]|1[0-2])-\d{2}(?:T.*)?$/);

  if (!match) {
    return period;
  }

  const [, year, month] = match;
  return `TRIM MOV ${month}/${year.slice(-2)}`;
};

function PrescricaoModal({ open, onClose, crm }: PrescricaoModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [prescricoes, setPrescricoes] = useState<PrescricaoDto[]>([]);
  const [availablePeriods, setAvailablePeriods] = useState<string[]>([]);
  const [availableMarkets, setAvailableMarkets] = useState<string[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);

  const [selectedPeriod, setSelectedPeriod] = useState<string>('');
  const [selectedMarket, setSelectedMarket] = useState<string>('');

  const [doctorName, setDoctorName] = useState<string>('');
  const [specialty, setSpecialty] = useState<string>('');
  const [state, setState] = useState<string>('');

  const fetchPrescricaoData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const filters: PrescricaoFilters = {};
      if (selectedPeriod) {
        filters.periodo = selectedPeriod;
      }
      if (selectedMarket) {
        filters.mercado = selectedMarket;
      }

      const response = await PrescricaoService.getByCrm(crm, filters);

      if (response.hasError) {
        setError(response.error || 'Erro ao buscar dados de prescrição');
        setPrescricoes([]);
        setTotalRecords(0);
        setDoctorName('');
        setSpecialty('');
        setState('');
      } else {
        setPrescricoes(response.data.items);
        setTotalRecords(response.data.totalRecords);
        setAvailablePeriods(response.data.availablePeriods);
        setAvailableMarkets(response.data.availableMarkets);

        if (response.data.items.length > 0) {
          const first = response.data.items[0];
          setDoctorName(first.medico);
          setSpecialty(first.especialidade);
          setState(first.estadoCrm);
        } else {
          setDoctorName('');
          setSpecialty('');
          setState('');
        }
      }
    } catch {
      setError('Erro ao buscar dados de prescrição');
      setDoctorName('');
      setSpecialty('');
      setState('');
    } finally {
      setLoading(false);
    }
  }, [crm, selectedMarket, selectedPeriod]);

  useEffect(() => {
    if (open) {
      fetchPrescricaoData();
    }
  }, [fetchPrescricaoData, open]);

  useEffect(() => {
    if (!open) {
      setError(null);
      setSelectedPeriod('');
      setSelectedMarket('');
    }
  }, [open]);

  const handleClearFilters = () => {
    setSelectedPeriod('');
    setSelectedMarket('');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        className={styles.title}
        sx={{
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
        }}
      >
        Dados de Prescrição - CRM {crm}
      </DialogTitle>

      <DialogContent dividers>
        {doctorName && (
          <Box className={styles.doctorDetails}>
            <Typography className={styles.doctorName}>{doctorName}</Typography>
            <Typography className={styles.specialty} color="textSecondary">
              {specialty} - {state}
            </Typography>
          </Box>
        )}

        {availablePeriods.length > 0 && (
          <Box className={styles.filters}>
            <FormControl className={styles.filter} variant="outlined" size="small">
              <InputLabel className={styles.filterLabel}>Período</InputLabel>
              <Select
                className={styles.filterSelect}
                value={selectedPeriod}
                onChange={e => setSelectedPeriod(e.target.value as string)}
                label="Período"
              >
                <MenuItem className={styles.filterOption} value="">
                  <em>Todos</em>
                </MenuItem>
                {availablePeriods.map(period => (
                  <MenuItem className={styles.filterOption} key={period} value={period}>
                    {formatPeriod(period)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl className={styles.filter} variant="outlined" size="small">
              <InputLabel className={styles.filterLabel}>Mercado</InputLabel>
              <Select
                className={styles.filterSelect}
                value={selectedMarket}
                onChange={e => setSelectedMarket(e.target.value as string)}
                label="Mercado"
              >
                <MenuItem className={styles.filterOption} value="">
                  <em>Todos</em>
                </MenuItem>
                {availableMarkets.map(market => (
                  <MenuItem className={styles.filterOption} key={market} value={market}>
                    {market}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {(selectedPeriod || selectedMarket) && (
              <Button size="small" onClick={handleClearFilters}>
                Limpar Filtros
              </Button>
            )}
          </Box>
        )}

        {loading && (
          <Box className={styles.loading}>
            <CircularProgress />
          </Box>
        )}

        {error && !loading && <Alert severity="error">{error}</Alert>}

        {!loading && !error && totalRecords === 0 && (
          <Box className={styles.emptyState}>
            <Typography variant="h6" gutterBottom>
              Nenhum dado de prescrição encontrado
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Dados são atualizados semanalmente via AWS Glue a partir do Databricks.
            </Typography>
          </Box>
        )}

        {!loading && !error && prescricoes.length > 0 && (
          <>
            <TableContainer className={styles.tableContainer} component={Paper}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell className={styles.tableHeaderCell}>Especialidade</TableCell>
                    <TableCell className={styles.tableHeaderCell}>Mercado</TableCell>
                    <TableCell className={styles.tableHeaderCell}>Período</TableCell>
                    <TableCell className={styles.tableHeaderCell}>Produto</TableCell>
                    <TableCell className={styles.tableHeaderCell} align="center">
                      <span className={styles.pxHeader}>
                        Unidades (PX)
                        <Tooltip
                          arrow
                          title="Total de unidades prescritas no período, considerando os canais farmácia e delivery."
                        >
                          <IconButton
                            aria-label="Explicação sobre unidades PX"
                            className={styles.pxInfoButton}
                            size="small"
                          >
                            <InfoOutlinedIcon className={styles.pxInfoIcon} />
                          </IconButton>
                        </Tooltip>
                      </span>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {prescricoes.map(item => (
                    <TableRow
                      key={`${item.crm}-${item.periodo}-${item.mercado}-${item.produto}-${item.px}-${item.ingestedAt}`}
                    >
                      <TableCell className={styles.tableCell}>{item.especialidade}</TableCell>
                      <TableCell className={styles.tableCell}>{item.mercado}</TableCell>
                      <TableCell className={styles.tableCell}>
                        {formatPeriod(item.periodo)}
                      </TableCell>
                      <TableCell className={styles.tableCell}>{item.produto}</TableCell>
                      <TableCell className={styles.tableCell} align="center">
                        <Chip label={item.px} size="small" color="primary" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography className={styles.summary} color="text.secondary">
              Mostrando {prescricoes.length} de {totalRecords} registros
              {prescricoes.length > 0 && (
                <> • Última atualização: {formatDate(prescricoes[0].ingestedAt)}</>
              )}
            </Typography>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default PrescricaoModal;
